/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Link';

const LinkManager: ComponentManagerFn<'Link'> = (
  platform: Platform,
): ComponentManager<'Link'> => {
  return {
    ...baseComponentManager<'Link'>(platform, 'Link'),
    type: 'Link',
    name: 'Link',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: (color: string) => {
          return ![
            'default',
            'info',
            'success',
            'warning',
            'ioGrey',
            'paper',
          ].includes(color);
        },
      },
      fullWidth: {
        label: 'Full Width',
        type: 'boolean',
        helpText:
          'If checked, the button will take up the full width of its container.',
      },
      text: {
        label: 'Text',
        type: 'string',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.text,
    category: 'Forms',
    icon: { iconName: 'external-link' },
    description: `**Overview**<br/>
The Link component creates a clickable text anchor, similar to a standard HTML \`<a>\` tag, but styled to match your application's theme. It is primarily used for navigation, opening external URLs, or triggering simple actions where a full "Button" component would be visually too heavy.
Use this component for secondary actions (like "Forgot Password?"), footer navigation, or inline references within a form.

**1. Visual Styling**<br/>
You can customize the appearance of the link to fit its context.
* **Text:** The visible label of the link (e.g., "Read More").
* **Color:** Sets the text color using the application's theme palette.
* **Supported Values:** **primary**, **secondary**, **error**.
* **Excluded Values:** Neutral tones like **default**, **ioGrey**, and **paper** are disabled to ensure links remain distinct and clickable.
* **Full Width:** A boolean toggle. 
    * **Checked:** The link behaves like a block element, stretching to fill its container. This is useful for sidebar menus.
    * **Unchecked:** The link behaves like an inline element, sitting naturally next to other text.

**2. Interactivity**<br/>
While it looks like a hyperlink, its behavior is defined by what you attach to it.
* **TriggerOnClick:** You must add a **TriggerOnClick** child component to define what happens when the link is clicked.
* **Actions:** Navigate to another page, open a URL in a new tab, or execute a script.
* **Tooltip:** Supports standard tooltip properties, allowing you to show a small helper box (e.g., the full URL or a description) on hover.

**3. Best Use Cases**<br/>
* **Authentication Forms:** "Forgot Password?" or "Sign Up" links below the login button.
* **Terms & Conditions:** "I agree to the **Terms of Service**" inside a checkbox label.
* **Breadcrumbs:** Simple navigational text trails at the top of a page.
* **Details Expansion:** A "View Details" link inside a data grid row.`,
  };
};

export default LinkManager;
